// src/components/PushRecipientStatusPanel.jsx
// Read-only visibility into Backend/push_recipient_status.php: who WOULD
// receive the Installation live-progress push (same role logic
// check_new_registrations.php already uses), and which of them currently
// has zero live subscriptions -- answers "these people's push tokens are
// dead" without changing the role-filtering behavior itself.
import React, { useEffect, useState } from "react";
import { FaSignal, FaSyncAlt, FaExclamationTriangle } from "react-icons/fa";
import { authFetch } from "../utils/api";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

export default function PushRecipientStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    authFetch(`${ABSOLUTE_HOST}/Backend/push_recipient_status.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) setStatus(d);
        else setError(d?.message || "Could not load recipient status.");
      })
      .catch(() => setError("Network error while loading recipient status."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const missing = (status?.recipients || []).filter((r) => !r.hasSubscription);

  return (
    <div className="panel" style={{ padding: "var(--sp-4)" }}>
      <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FaSignal size={14} style={{ color: "var(--accent)" }} /> Installation Push: Recipient Status
        </h2>
        <button onClick={load} className="btn btn-ghost btn-sm" disabled={loading}>
          <FaSyncAlt size={11} /> Refresh
        </button>
      </div>
      <p className="field-hint" style={{ marginBottom: "var(--sp-4)" }}>
        Who currently receives the "Installation Registrations" live-progress push (per the roles configured
        below), and whether their device has a live subscription. A member here without a subscription hasn't
        enabled notifications on their device yet, or their subscription has gone stale -- not a bug in who's
        configured to receive it.
      </p>

      {loading ? (
        <div className="empty-state"><span className="spinner lg" /></div>
      ) : error ? (
        <div className="error-banner">{error}</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)", marginBottom: "var(--sp-4)" }}>
            <div className="panel tile">
              <span className="tile-label">Configured Recipients</span>
              <span className="tile-value num">{status?.totalConfigured ?? 0}</span>
            </div>
            <div className="panel tile">
              <span className="tile-label">Missing a Subscription</span>
              <span className="tile-value num" style={{ color: missing.length ? "var(--warning)" : "var(--text-1)" }}>
                {status?.totalMissingSubscription ?? 0}
              </span>
            </div>
          </div>

          {missing.length > 0 && (
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>SLIIT ID</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((r) => (
                    <tr key={r.sliitId}>
                      <td style={{ fontWeight: 600 }}>
                        <FaExclamationTriangle size={11} style={{ color: "var(--warning)", marginRight: 6 }} />
                        {r.name || r.sliitId}
                      </td>
                      <td>{r.role}</td>
                      <td className="num">{r.sliitId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
